import { useQueries } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';
import { deflateSync } from 'fflate';
import { getPrincipalQuery } from 'src/selectors/getPrincipalQuery';
import { isAuthorizedQuery } from 'src/selectors/isAuthorizedQuery';
import { DpcmService } from 'src/services/DpcmService';
import { DesignConductorService, MatcherType, DesignCreationType } from 'src/services/DesignConductorService';
import { TemplateDiscoveryService } from 'src/services/TemplateDiscoveryService';
import { DocumentSourceQuery } from 'src/hooks/useDocumentSource';

type CimpressDocument = Record<string, unknown>;

interface ImageInfo {
    originalSourceUrl: string;
    previewUrl: string;
    printUrl: string;
    height: number;
}

interface AssetImage {
    image: ImageInfo;
    purpose: string;
}

interface DesignPersonalizationData {
    designPersonalizationContextVersion: number;
    text: unknown[];
    images: Array<AssetImage>;
}

interface DpcmResponse {
    designPersonalizationContextId?: string;
    designPersonalizationData?: DesignPersonalizationData;
    [key: string]: unknown;
}

interface MatchingV2Response {
    previewUrl: string;
    fullProductSpecification: Record<string, unknown>;
    [key: string]: unknown;
}

interface MatchingVisualizerLoadParams {
    documentSourceUri: string;
    productKey: string;
    productVersion: number;
    culture: string;
    fullProductSpecification: Record<string, string>;
    preferredProductSpecification?: Record<string, string>;
    pdsResultUri?: string;
    dpcId?: string;
}

interface DesignTypeMatchResult {
    isLoading: boolean;
    error: Error | null;
    data: MatchingV2Response | undefined;
}

interface MatchingVisualizerResults {
    dpcmResult: {
        isLoading: boolean;
        error: Error | null;
        data: DpcmResponse | undefined;
    };
    pdsResultPreview: {
        isLoading: boolean;
        error: Error | null;
        previewUrl: string | null;
    };
    templateDiscoveryResult: {
        isLoading: boolean;
        error: Error | null;
        designConceptId: string | null;
    };
    matchingResults: {
        [K in MatcherType]: {
            isLoading: boolean;
            error: Error | null;
            data: MatchingV2Response[] | undefined;
            // For template matcher, include design type specific results
            designTypes?: {
                [D in DesignCreationType]: DesignTypeMatchResult;
            };
        };
    };
    documentSourcePreviewUrl: string | null;
    isLoading: boolean;
    hasError: boolean;
    errors: (Error | null)[];
    warnings: string[];
}

const encodeCimDoc = ({ cimDoc }: { cimDoc: CimpressDocument }): string => {
    const documentJson = JSON.stringify(cimDoc);
    const encoder = new TextEncoder();

    const encodedValue = encoder.encode(documentJson);
    const valueDeflated = deflateSync(encodedValue);

    if (typeof btoa === 'undefined') {
        return Buffer.from(valueDeflated).toString('base64');
    }

    return btoa(String.fromCharCode(...valueDeflated));
};

export const generatePreviewURI = async (document: CimpressDocument, height = '400'): Promise<string> => {
    const encodedDocumentStr = encodeCimDoc({ cimDoc: document });

    const instructionsURI = `https://instructions.documents.cimpress.io/v3/instructions:preview`;
    const renderingURL = `https://rendering.documents.cimpress.io/v2/example/preview?height=${height}&instructions_uri=${encodeURIComponent(instructionsURI)}`;

    const formData = new FormData();
    const instructionsPostData = JSON.stringify({ documentUri: encodedDocumentStr });

    formData.append('instructionsPostData', instructionsPostData);

    const response = await fetch(renderingURL, {
        method: 'POST',
        body: formData,
    });

    if (!response.ok) {
        throw new Error(`Failed to generate preview: ${response.status} ${response.statusText}`);
    }

    const blob = await response.blob();

    return URL.createObjectURL(blob);
};

export const useMatchingVisualizerLoad = (
    params: MatchingVisualizerLoadParams | null,
    documentSourceQuery: DocumentSourceQuery,
): MatchingVisualizerResults => {
    const canonicalId = useRecoilValue(getPrincipalQuery);
    const accessToken = useRecoilValue(isAuthorizedQuery);

    const enabled = !!params && !!documentSourceQuery.data && !documentSourceQuery.error && !!canonicalId && !!accessToken;

    const queries = useQueries({
        queries: [
            // 1. Create Design Personalization Context (skip if dpcId is provided)
            {
                queryKey: ['dpcm', params?.documentSourceUri, params?.dpcId, canonicalId, accessToken],
                queryFn: async (): Promise<DpcmResponse> => {
                    if (!params || !canonicalId || !accessToken) throw new Error('Missing parameters');

                    // If dpcId is provided, return it directly without calling DPCM
                    if (params.dpcId) {
                        return {
                            designPersonalizationContextId: params.dpcId,
                        };
                    }

                    return DpcmService.createDesignPersonalizationContext(
                        accessToken,
                        canonicalId,
                        {
                            strategies: [
                                {
                                    strategy: 'documentUrl',
                                    data: params.documentSourceUri,
                                },
                            ],
                        },
                    );
                },
                enabled,
                refetchOnWindowFocus: false,
                suspense: false,
                retry: false,
            },
            // 2. Fetch Site Result (optional)
            {
                queryKey: ['siteResult', params?.pdsResultUri],
                queryFn: async (): Promise<string> => {
                    if (!params?.pdsResultUri) throw new Error('Missing Site Result URI');

                    const response = await fetch(params.pdsResultUri);
                    const data = await response.json() as CimpressDocument;

                    return generatePreviewURI(data);
                },
                enabled: enabled && !!params?.pdsResultUri,
                refetchOnWindowFocus: false,
                suspense: false,
                retry: false,
            },
        ],
    });

    const [dpcmQuery, pdsResultQuery] = queries;

    const templateIdentifier = documentSourceQuery.data?.templateIdentifier;

    // Check if this is a static design (has TEMPLATE_TOKEN)
    const isStaticDesign = templateIdentifier?.type === 'TEMPLATE_TOKEN';
    const templateToken = isStaticDesign
        ? templateIdentifier.value
        : undefined;

    // Call Template Discovery for static designs to get design concept
    const templateDiscoveryQuery = useQueries({
        queries: [
            {
                queryKey: ['templateDiscovery', templateToken, accessToken],
                queryFn: async (): Promise<string | null> => {
                    if (!templateToken || !accessToken) {
                        throw new Error('Missing template token or access token');
                    }

                    const ensembleLines = await TemplateDiscoveryService.getEnsembleLinesFromTemplateToken(
                        templateToken,
                        accessToken,
                    );

                    return ensembleLines[0]?.ensembles[0]?.designProperties?.isProminentColorVariant !== undefined
                        ? ensembleLines[0].designConceptId
                        : null;
                },
                enabled: enabled && isStaticDesign && !!templateToken,
                refetchOnWindowFocus: false,
                suspense: false,
                retry: false,
            },
        ],
    })[0];

    const documentSourcePreviewQuery = useQueries({
        queries: [
            {
                queryKey: ['documentSourcePreview', documentSourceQuery.data],
                queryFn: async (): Promise<string> => {
                    const cimDoc = documentSourceQuery.data as CimpressDocument;

                    return generatePreviewURI(cimDoc);
                },
                enabled: enabled && !!documentSourceQuery.data,
                refetchOnWindowFocus: false,
                suspense: false,
                retry: false,
            },
        ],
    })[0];

    const documentSourcePreviewUrl = documentSourcePreviewQuery.data ?? null;

    // 3. Call Matching v2 for logo (depends on DPCM and Document Source results)
    const logoQuery = useQueries({
        queries: [{
            queryKey: [
                'matching',
                'logo',
                params?.productKey,
                params?.productVersion,
                params?.culture,
                params?.fullProductSpecification,
                params?.preferredProductSpecification,
                dpcmQuery.data?.designPersonalizationContextId,
                templateIdentifier,
            ],
            queryFn: async (): Promise<MatchingV2Response[]> => {
                if (!params || !dpcmQuery.data) {
                    throw new Error('Missing dependencies for matching call');
                }

                // For logo matching, pass all options (required + preferred) as productOptions
                const allOptions = {
                    ...params.fullProductSpecification,
                    ...(params.preferredProductSpecification || {}),
                };
                const result = await DesignConductorService.getLogoMatching({
                    productKey: params.productKey,
                    productVersion: params.productVersion.toString(),
                    culture: params.culture,
                    productOptions: allOptions,
                    designPersonalizationContextId: dpcmQuery.data.designPersonalizationContextId || undefined,
                });

                // Logo returns single object, normalize to array for consistent handling
                return [result];
            },
            enabled: enabled && !!dpcmQuery.data && !dpcmQuery.error,
            refetchOnWindowFocus: false,
            suspense: false,
            retry: false,
        }],
    })[0];

    // 4. Call Template Matching for each design type in parallel
    const designTypes: DesignCreationType[] = ['Static', 'Dynamic', 'AutoLayout'];
    const templateDesignTypeQueries = useQueries({
        queries: designTypes.map((designType) => ({
            queryKey: [
                'matching',
                'template',
                designType,
                params?.productKey,
                params?.productVersion,
                params?.culture,
                params?.fullProductSpecification,
                params?.preferredProductSpecification,
                dpcmQuery.data?.designPersonalizationContextId,
                templateIdentifier,
            ],
            queryFn: async (): Promise<MatchingV2Response | null> => {
                if (!params || !dpcmQuery.data) {
                    throw new Error('Missing dependencies for matching call');
                }

                if (!templateIdentifier?.value) {
                    throw new Error('A template identifier is required for template type matching');
                }

                const baseRequest = {
                    productKey: params.productKey,
                    productVersion: params.productVersion.toString(),
                    culture: params.culture,
                    productOptions: params.fullProductSpecification,
                    preferredOptions: params.preferredProductSpecification,
                    designPersonalizationContextId: dpcmQuery.data.designPersonalizationContextId || undefined,
                    templateIdentifier: templateIdentifier.value,
                    designCreationType: designType,
                };

                const results = await DesignConductorService.getTemplateMatching(baseRequest);
                // Return the first result for the specific design type

                return results?.[0] || null;
            },
            enabled: enabled && !!dpcmQuery.data && !dpcmQuery.error,
            refetchOnWindowFocus: false,
            suspense: false,
            retry: false,
        })),
    });

    const [staticQuery, dynamicQuery, autoLayoutQuery] = templateDesignTypeQueries;

    const warnings: string[] = [];
    const hasDpcId = !!dpcmQuery.data?.designPersonalizationContextId;

    if (dpcmQuery.data && !hasDpcId && !dpcmQuery.error) {
        warnings.push('Document has no Design Personalization Context. Matching will proceed without DPC ID.');
    }

    if (templateDesignTypeQueries.every((q) => q.error !== null) && logoQuery.error !== null) {
        warnings.push('All matching requests failed. Please check each specific matcher for details.');
    }

    const isLoading = params
        ? (dpcmQuery.isLoading || logoQuery.isLoading
             || templateDesignTypeQueries.some((q) => q.isLoading && !dpcmQuery.error && !documentSourceQuery.error))
        : false;
    // Only include shared errors (DPCM and Document Source) in the general error display
    const sharedErrors = [dpcmQuery.error];
    const hasError = sharedErrors.some((error) => error !== null);

    return {
        dpcmResult: {
            isLoading: params ? dpcmQuery.isLoading : false,
            error: dpcmQuery.error as Error | null,
            data: dpcmQuery.data,
        },
        pdsResultPreview: {
            isLoading: params?.pdsResultUri ? pdsResultQuery.isLoading : false,
            error: pdsResultQuery.error as Error | null,
            previewUrl: pdsResultQuery.data ?? null,
        },
        templateDiscoveryResult: {
            isLoading: params && isStaticDesign ? templateDiscoveryQuery.isLoading : false,
            error: templateDiscoveryQuery.error as Error | null,
            designConceptId: templateDiscoveryQuery.data ?? null,
        },
        matchingResults: {
            template: {
                isLoading: params
                    ? (templateDesignTypeQueries.some((q) => q.isLoading)
                        && !dpcmQuery.error)
                    : false,
                error: null, // We'll handle errors per design type
                data: undefined, // Legacy field, we use designTypes now
                designTypes: {
                    Static: {
                        isLoading: params
                            ? (staticQuery.isLoading && !dpcmQuery.error)
                            : false,
                        error: staticQuery.error as Error | null,
                        data: staticQuery.data || undefined,
                    },
                    Dynamic: {
                        isLoading: params
                            ? (dynamicQuery.isLoading && !dpcmQuery.error)
                            : false,
                        error: dynamicQuery.error as Error | null,
                        data: dynamicQuery.data || undefined,
                    },
                    AutoLayout: {
                        isLoading: params
                            ? (autoLayoutQuery.isLoading && !dpcmQuery.error)
                            : false,
                        error: autoLayoutQuery.error as Error | null,
                        data: autoLayoutQuery.data || undefined,
                    },
                },
            },
            logo: {
                isLoading: params ? (logoQuery.isLoading && !dpcmQuery.error) : false,
                error: logoQuery.error as Error | null,
                data: logoQuery.data,
            },
        },
        documentSourcePreviewUrl,
        isLoading,
        hasError,
        errors: sharedErrors as (Error | null)[],
        warnings,
    };
};
